import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { dropTask } from 'ember-concurrency'
import perform from 'ember-concurrency/helpers/perform'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import type { IntlService } from 'ember-intl'
import type TableToCsv from 'teacher-ui-client/services/table-to-csv'
import type WindowService from 'teacher-ui-client/services/window'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { ActionMenu } from 'teacher-ui-client/components/base/action-menu'
import { ActionMenuItem } from 'teacher-ui-client/components/base/action-menu/item'
import type { PhosphorIconName } from 'phosphor-icons'

interface Args {
  exportOptions?: ExportOption[]
  disabled?: boolean
}

export enum ExportType {
  CSV = 'csv',
  print = 'print',
}

/**
 * Provide an exportType to match with a default export option, and override other attributes as you need.
 */
export type ExportOption = ExportType | ({ exportType: ExportType } & Partial<ExportOptionItem>)

interface ExportOptionItem {
  exportType: ExportType
  exportAction: () => void
  label: string
  icon: PhosphorIconName
}

export function isExportOption(maybeOption: unknown): maybeOption is ExportOption {
  if (typeof maybeOption === 'string') return Object.values(ExportType).includes(maybeOption as ExportType)
  return (
    maybeOption !== null &&
    typeof maybeOption === 'object' &&
    'exportType' in maybeOption &&
    typeof maybeOption.exportType === 'string' &&
    Object.values(ExportType).includes(maybeOption.exportType as ExportType)
  )
}

export class ReportingExportSelect extends Component<Args> {
  @service window!: WindowService

  @service intl!: IntlService

  @service tableToCsv!: TableToCsv

  get exportOptions() {
    return this.args.exportOptions ?? []
  }

  get exportTypeDefaults(): Record<ExportType, ExportOptionItem> {
    return {
      [ExportType.CSV]: {
        exportType: ExportType.CSV,
        label: this.intl.t('components.reporting.exportSelect.labels.csv'),
        icon: 'phosphor-file-x-fill',
        exportAction: () => {
          const dateFormat = this.intl.formatDate(new Date()).replace(/\//g, '-')
          const filename = `export_${dateFormat}.csv`
          this.tableToCsv.downloadCsvFrom('[exportable]:not([exportable=false])', { filename })
        },
      },
      [ExportType.print]: {
        exportType: ExportType.print,
        label: this.intl.t('components.reporting.exportSelect.labels.print'),
        icon: 'phosphor-printer-fill',
        exportAction: () => this.window.getWindow.print(),
      },
    }
  }

  /**
   * Fill each given export option with label/icon based on its export type, to use for display in the dropdown
   */
  get exportOptionItems(): ExportOptionItem[] {
    const { exportTypeDefaults } = this
    return this.exportOptions?.map((exportOption) => {
      if (typeof exportOption === 'string') {
        // just an export type string was given, so use all the defaults for that export type
        return exportTypeDefaults[exportOption]
      }
      // an export type partial was given, apply defaults and override with its customisations
      return {
        ...exportTypeDefaults[exportOption.exportType],
        ...exportOption, // placing it after allows for defaults to be overridden as needed
      }
    })
  }

  get disabled() {
    return isEmpty(this.exportOptions) || this.args.disabled || this.exportTask.isRunning
  }

  /**
   * An export task to run the export action in, so that we can disable the button while running if it is async.
   */
  exportTask = dropTask(async (exportAction: () => Promise<void> | void) => {
    await exportAction()
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <PopoverStandardDropdown @disabled={{this.disabled}} as |dropdown _status actions|>
      <UiButton
        data-test-export-button
        disabled={{this.disabled}}
        @appearance={{ButtonAppearance.muted}}
        aria-haspopup="menu"
        {{dropdown.makeTrigger}}
      >
        {{t "components.reporting.exportSelect.buttonLabel"}}
        <PhosphorIcon @icon="phosphor-caret-down-bold" class="h-3.5 w-3.5" />
      </UiButton>
      <dropdown.content>
        <ActionMenu data-test-export-options>
          {{#each this.exportOptionItems as |option|}}
            <ActionMenuItem
              data-test-export-option={{option.exportType}}
              {{on "click" (perform this.exportTask option.exportAction)}}
              {{on "click" actions.close}}
            >
              <PhosphorIcon @icon={{option.icon}} />
              {{option.label}}
            </ActionMenuItem>
          {{/each}}
        </ActionMenu>
      </dropdown.content>
    </PopoverStandardDropdown>
  </template>
}

export default ReportingExportSelect
